import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, Image, StyleSheet, TouchableOpacity, Platform, Modal } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../../../styles/variablesCss';
import CartItemControl from './CartItemControl';
import { getEnvioGratisInfo } from '../../../../Config/Const';
import { formatPrecio } from '../../../../utils/formatPrice';

// Componente para la imagen del producto con botón de eliminar en hover
const ProductImageWithDeleteButton = ({ producto, plu, cantidad, onRefresh }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Los eventos de hover solo funcionan en web
  const hoverProps = Platform.OS === 'web' ? {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  } : {};

  return (
    <View 
      style={styles.imageContainer} 
      {...hoverProps}
    >
      {producto.foto ? (
        <Image
          source={{ uri: producto.foto }}
          style={styles.itemImage}
          resizeMode="contain"
        />
      ) : (
        <View style={styles.placeholderImage}>
          <MaterialCommunityIcons name="package-variant" size={24} color={colores.grisClaro} />
        </View>
      )}
      
      {/* Botón eliminar siempre visible en web */}
      {Platform.OS === 'web' && (
        <CartItemControl
          plu={plu}
          cantidad={cantidad}
          style={styles.deleteHoverButton}
          onSuccess={onRefresh}
        >
          <MaterialCommunityIcons name="delete-outline" size={18} color={colores.blanco} />
        </CartItemControl>
      )}
      
      {/* Botón eliminar visible siempre en móvil */}
      {Platform.OS !== 'web' && (
        <CartItemControl
          plu={plu}
          cantidad={cantidad}
          style={styles.deleteMobileButton}
          onSuccess={onRefresh}
        >
          <MaterialCommunityIcons name="delete-outline" size={18} color={colores.blanco} />
        </CartItemControl>
      )}
    </View>
  );
};

/**
 * Componente reutilizable para mostrar los productos del carrito
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.productos - Array de productos en el carrito
 * @param {Number} props.total - Total del carrito (opcional, si no se proporciona se calcula)
 * @param {Number} props.subtotal - Subtotal del carrito
 * @param {Number} props.descuentos - Descuentos aplicados
 * @param {Function} props.onRefresh - Función a ejecutar para refrescar el carrito
 * @param {Function} props.onCheckout - Función a ejecutar al hacer checkout
 * @param {Function} props.onVaciarCarrito - Función a ejecutar para vaciar el carrito
 */
const TemplateCarrito = ({
  productos = [],
  total = null,
  subtotal = null,
  descuentos = null,
  onRefresh,
  onCheckout,
  onVaciarCarrito,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  // Estados para feedback por ítem: borde verde y bloqueo temporal
  const [highlightedItems, setHighlightedItems] = useState({});
  const [disabledItems, setDisabledItems] = useState({});
  // Detectar ancho para comportamiento sticky en web móvil
  const [viewportWidth, setViewportWidth] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const isSmallWeb = Platform.OS === 'web' && viewportWidth < 768;
  const isExtraSmallWeb = Platform.OS === 'web' && viewportWidth < 576;

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  // Disparar feedback de 500ms (igual que AddCarrito) y refrescar
  // type: 'add' | 'sub' (solo coloreamos en 'add')
  const triggerItemFeedback = (plu, type = 'add') => {
    if (type === 'add') {
      setHighlightedItems((prev) => ({ ...prev, [plu]: true }));
    }
    setDisabledItems((prev) => ({ ...prev, [plu]: true }));
    setTimeout(() => {
      if (type === 'add') {
        setHighlightedItems((prev) => ({ ...prev, [plu]: false }));
      }
      setDisabledItems((prev) => ({ ...prev, [plu]: false }));
      if (onRefresh) onRefresh();
    }, 500);
  };

  const handleVaciarCarrito = () => {
    setModalVisible(false);
    if (onVaciarCarrito) {
      onVaciarCarrito();
    }
  };

  // Si no hay productos, mostrar carrito vacío
  if (!productos || productos.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="cart-outline" size={50} color={colores.grisClaro} />
        <Text style={styles.emptyText}>Tu carrito está vacío</Text>
      </View>
    );
  }

  // Calcular total si no se proporciona
  const calcularTotal = () => {
    if (total !== null) return total;

    return productos.reduce((acc, producto) => {
      return acc + (producto.precio * (producto.cantCarrito || 1));
    }, 0);
  };

  const envioGratisInfo = getEnvioGratisInfo({ total: calcularTotal(), subtotal });

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.itemsContainer}
        contentContainerStyle={[
          styles.itemsContent,
          Platform.OS === 'web' && isSmallWeb ? { paddingBottom: 180 } : null,
        ]}
      >
        {productos.map((producto) => (
          <View
            key={producto.plu}
            style={styles.itemContainer}
          >
            <View style={styles.itemRow}>
              {/* Imagen del producto con botón eliminar en hover */}
              <ProductImageWithDeleteButton 
                producto={producto} 
                plu={producto.plu}
                cantidad={-(producto.cantCarrito || 1)}
                onRefresh={() => onRefresh && onRefresh()}
              />
                            {/* Contenido principal: info producto y cantidad en disposición flexbox */}
              <View style={styles.contentContainer}>
                {/* Row para organizar info del producto y controles de cantidad */}
                <View style={styles.productControlsRow}>
                  {/* Info del producto */}
                  <View style={styles.infoContainer}>
                    <Text style={styles.itemNombre} numberOfLines={2} ellipsizeMode="tail">
                      {producto.descripcion || producto.nombre || 'Producto'}
                    </Text>
     
                    <View style={styles.precioRow}>
                      <Text style={styles.itemPrecio}>
                        $ {formatPrecio(parseFloat(producto.precio) * (producto.cantCarrito || 1), true)}
                      </Text>
                    </View>
                  </View>
                  
                  {/* Controles de cantidad en vertical */}
                  <View
                    style={[
                      styles.cantidadContainerVertical,
                      highlightedItems[producto.plu] ? styles.cantidadContainerHighlighted : null,
                    ]}
                    pointerEvents={disabledItems[producto.plu] ? 'none' : 'auto'}
                  >
                    <CartItemControl
                      plu={producto.plu}
                      cantidad={producto.minStock || 1}
                      style={[
                        styles.cantidadButtonVertical,
                        highlightedItems[producto.plu] ? styles.cantidadButtonVerticalHighlighted : null,
                      ]}
                      onSuccess={() => triggerItemFeedback(producto.plu, 'add')}
                    >
                      <Text style={[
                        styles.cantidadButtonText,
                        highlightedItems[producto.plu] ? styles.cantidadButtonTextHighlighted : null,
                      ]}>+</Text>
                    </CartItemControl>

                    <Text style={[
                      styles.cantidadText,
                      highlightedItems[producto.plu] ? styles.cantidadButtonTextHighlighted : null,
                    ]}>{producto.cantCarrito || 1}</Text>

                    <CartItemControl
                      plu={producto.plu}
                      cantidad={-(producto.minStock || 1)}
                      style={[
                        styles.cantidadButtonVertical,
                        highlightedItems[producto.plu] ? styles.cantidadButtonVerticalHighlighted : null,
                      ]}
                      onSuccess={() => triggerItemFeedback(producto.plu, 'sub')}
                    >
                      <Text style={[
                        styles.cantidadButtonText,
                        highlightedItems[producto.plu] ? styles.cantidadButtonTextHighlighted : null,
                      ]}>-</Text>
                    </CartItemControl>
                  </View>
                </View>
              </View>
            </View>
          </View>
        ))}
      </ScrollView>

      {/* Barra de progreso para envío gratis con botón de vaciar */}
      <View style={[
        styles.envioGratisContainer,
        isSmallWeb ? styles.envioGratisContainerSticky : null,
        isExtraSmallWeb ? styles.envioGratisContainerExtraSmall : null,
      ]}>
        <View style={styles.envioGratisHeader}>
          <View style={styles.progressSection}>
            <View style={styles.progressBarContainer}>
              <View
                style={[
                  styles.progressBar,
                  {
                    width: `${envioGratisInfo.progress * 100}%`,
                    backgroundColor: envioGratisInfo.isGratis ? 'green' : 'red'
                  }
                ]}
              />
            </View>
            <View style={styles.envioGratisTextContainer}>
              {envioGratisInfo.isGratis ? (
                <View style={styles.envioGratisMessage}>
                  <MaterialCommunityIcons name="truck-fast" size={16} color="green" />
                  <Text style={styles.envioGratisBadgeText}>Envío Gratis</Text>
                </View>
              ) : (
                <Text style={styles.envioGratisText}>
                  ${envioGratisInfo.montoFaltanteFormateado} para envío gratis
                </Text>
              )}
            </View>
          </View>
          {onVaciarCarrito && (
            <TouchableOpacity
              style={styles.vaciarCarritoButtonSmall}
              onPress={() => setModalVisible(true)}
            >
              <MaterialCommunityIcons name="delete-sweep" size={18} color={colores.rojo} />
              <Text style={styles.vaciarCarritoButtonSmallText}>Vaciar</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Total, subtotal, descuentos y botón de checkout */}
      <View style={[
        styles.totalContainer,
        Platform.OS === 'web' && isSmallWeb ? styles.totalContainerSticky : null,
        Platform.OS === 'web' && isExtraSmallWeb ? styles.totalContainerExtraSmall : null,
      ]}>
        {subtotal > 0 && (
          <View style={styles.subtotalContainer}>
            <Text style={styles.subtotalLabel}>Subtotal:</Text>
            <Text style={styles.subtotalValue}>$ {formatPrecio(parseFloat(subtotal), true)}</Text>
          </View>
        )}
        
        {descuentos > 0 && (
          <View style={styles.descuentosContainer}>
            <Text style={styles.descuentosLabel}>Descuento:</Text>
            <Text style={styles.descuentosValue}>- $ {formatPrecio(parseFloat(descuentos), true)}</Text>
          </View>
        )}
        
        <View style={styles.totalLine}>
          <Text style={styles.totalText}>Total:</Text>
          <Text style={styles.totalValue}>$ {formatPrecio(calcularTotal(), true)}</Text>
        </View>
        
        <TouchableOpacity
          style={styles.checkoutButton}
          onPress={onCheckout}
        >
          <MaterialCommunityIcons name="cart-check" size={20} color={colores.blanco} />
          <Text style={styles.checkoutButtonText}>FINALIZAR COMPRA</Text>
        </TouchableOpacity>
      </View>

      {/* Modal de confirmación */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <MaterialCommunityIcons name="alert-circle-outline" size={40} color={colores.rojo} />
            </View>
            <Text style={styles.modalTitle}>Vaciar carrito</Text>
            <Text style={styles.modalMessage}>
              ¿Estás seguro de que deseas eliminar todos los productos del carrito?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={styles.modalButtonTextCancel}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonConfirm]}
                onPress={handleVaciarCarrito}
              >
                <Text style={styles.modalButtonTextConfirm}>Vaciar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colores.blanco,
    paddingHorizontal: 15,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      borderRadius: 4,
    } : {}),
  },
  subtotalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 5,
    marginBottom: 5,
  },
  subtotalLabel: {
    fontSize: 14,
    color: colores.textoSecundario,
  },
  subtotalValue: {
    fontSize: 14,
    color: colores.textoSecundario,
  },
  descuentosContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 5,
    marginBottom: 5,
  },
  descuentosLabel: {
    fontSize: 14,
    color: colores.verde,
  },
  descuentosValue: {
    fontSize: 14,
    color: colores.verde,
    fontWeight: 'bold',
  },
  totalLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 5,
    marginBottom: 10,
  },
  totalText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
  },
  totalValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.rojo,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 200,
  },
  emptyText: {
    color: colores.grisOscuro,
    fontSize: 16,
    marginTop: 15,
  },
  itemsContainer: {
    flex: 1,
  },
  itemContainer: {
    paddingHorizontal: 15,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
    backgroundColor: colores.blanco,
    ...(Platform.OS === 'web' ? {
      marginBottom: 2,
    } : {}),
  },
  itemContainerHighlighted: {
    borderWidth: 2,
    borderColor: colores.verde,
    borderRadius: 6,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  contentContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  productControlsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  imageContainer: {
    width: 60,
    height: 80,
    borderRadius: 5,
    overflow: 'hidden',
    marginRight: 10,
    backgroundColor: colores.fondoGris || '#f5f5f5',
    position: 'relative',  // Para posicionar el botón de eliminar
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colores.fondoGris || '#f5f5f5',
  },
  itemImage: {
    width: '100%',
    height: '100%',
  },
  infoContainer: {
    flex: 1,
    marginRight: 10,
  },
  itemNombre: {
    fontSize: 14,
    fontWeight: '500',
    color: colores.textoPrincipal,
    marginBottom: 5,
    textTransform: 'uppercase',
  },
  itemMarca: {
    fontSize: 12,
    color: colores.grisOscuro,
    marginBottom: 5,
  },
  precioRow: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginTop: 5,
  },
  itemPrecio: {
    fontSize: 16,
    fontWeight: 'bold',
    marginRight: 15,
  },
  itemSubtotal: {
    fontSize: 14,
    color: colores.textoPrincipal,
  },
  removeButton: {
    padding: 8,
    backgroundColor: colores.fondoGris || '#f5f5f5',
    borderRadius: 25,
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cantidadContainer: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cantidadContainerVertical: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colores.fondoGris || '#f5f5f5',
    borderRadius: 10,
    padding: 5,
    marginLeft: 10,
  },
  cantidadContainerHighlighted: {
    backgroundColor: 'green',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'green',
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 0 2px rgba(0,0,0,0.05) inset' } : {}),
  },
  cantidadButtonVerticalHighlighted: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  cantidadButtonTextHighlighted: {
    color: colores.blanco,
  },
  cantidadLabel: {
    marginRight: 10,
    flexDirection: 'column',
  },
  cantidadLabelText: {
    fontSize: 14,
    color: colores.textoPrincipal,
    marginBottom: 3,
  },
  minStock: {
    fontSize: 11,
    color: colores.grisOscuro,
    fontStyle: 'italic',
  },
  cantidadControls: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colores.fondoGris || '#f5f5f5',
    borderRadius: 25,
    padding: 5,
  },
  cantidadButton: {
    backgroundColor: colores.blanco,
    borderRadius: 20,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cantidadButtonVertical: {
    backgroundColor: colores.blanco,
    borderRadius: 20,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cantidadButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
  },
  cantidadText: {
    fontSize: 14,
    minWidth: 35,
    textAlign: 'center',
    color: colores.textoPrincipal,
    fontWeight: '500',
  },
  envioGratisContainer: {
    marginVertical: 5,
    backgroundColor: Platform.OS === 'web' ? colores.fondoGris || '#f5f5f5' : 'transparent',
    borderRadius: 8,
    padding: 12,
  },
  envioGratisContainerSticky: {
    position: 'sticky',
    bottom: 180,
    backgroundColor: colores.blanco,
    zIndex: 4,
    marginVertical: 0,
    borderRadius: 0,
    borderTopWidth: 1,
    borderTopColor: colores.grisClaro,
    ...(Platform.OS === 'web' ? { boxShadow: '0 -2px 8px rgba(0,0,0,0.08)' } : {}),
  },
  envioGratisContainerExtraSmall: {
    padding: 8,
    bottom: 160,
  },
  envioGratisHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  progressSection: {
    flex: 1,
    marginRight: 10,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: colores.grisClaro,
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 5,
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  envioGratisTextContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  envioGratisText: {
    fontSize: 14,
    color: colores.textoPrincipal,
  },
  envioGratisMessage: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e8f5e9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  envioGratisBadgeText: {
    color: 'green',
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  totalContainer: {
    borderTopWidth: 1,
    borderTopColor: colores.borde,
    paddingVertical: 12,
  },
  totalContainerSticky: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: colores.blanco,
    paddingHorizontal: 10,
    paddingBottom: 100,
    zIndex: 5,
    borderTopWidth: 1,
    borderTopColor: colores.borde,
    ...(Platform.OS === 'web' ? { boxShadow: '0 -2px 8px rgba(0,0,0,0.08)' } : {}),
  },
  totalContainerExtraSmall: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    paddingBottom: 100,
  },
  itemsContent: {
    paddingBottom: 0,
  },
  checkoutButton: {
    backgroundColor: colores.rojo,
    padding: 12,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    marginTop: 5,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  checkoutButtonText: {
    color: colores.blanco,
    fontWeight: 'bold',
    fontSize: 14,
    marginLeft: 5,
  },
  vaciarCarritoButtonSmall: {
    backgroundColor: colores.blanco,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colores.rojo,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  vaciarCarritoButtonSmallText: {
    color: colores.rojo,
    fontSize: 11,
    fontWeight: '600',
    marginLeft: 4,
  },
  deleteHoverButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: colores.rojo,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.9,
  },
  deleteMobileButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: colores.rojo,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.9,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: colores.blanco,
    borderRadius: 12,
    padding: 24,
    width: '85%',
    maxWidth: 400,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 8,
      },
    }),
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colores.textoPrincipal,
    textAlign: 'center',
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 15,
    color: colores.textoSecundario,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  modalButtonCancel: {
    backgroundColor: colores.fondoGris || '#f5f5f5',
    borderWidth: 1,
    borderColor: colores.grisClaro,
  },
  modalButtonConfirm: {
    backgroundColor: colores.rojo,
  },
  modalButtonTextCancel: {
    color: colores.textoPrincipal,
    fontSize: 15,
    fontWeight: '600',
  },
  modalButtonTextConfirm: {
    color: colores.blanco,
    fontSize: 15,
    fontWeight: '600',
  },
});

export default TemplateCarrito;
